// Requêtes des écrans des actions sociales (lot L7, M20, TanStack Query), clés sous « social ». Types de
// packages/contrats/src/social.ts ; le serveur refait tous les contrôles (rôle, habilitation, module, états).
import type {
  ActivationSociale,
  CreationDemandeSociale,
  CreationProgrammeSocial,
  DecisionSocialeSaisie,
  DemandeSociale,
  EvaluationCriteres,
  ProgrammeSocial,
  ResumeDemandeSociale,
  SuiviSocialSaisi,
  SyntheseSociale,
  VersionCriteresSocial,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { telechargerBlob } from '../metier/csv';

export interface Liste<T> {
  elements: T[];
}

export const INVALIDER_SOCIAL: readonly (readonly unknown[])[] = [['social']];

const lire =
  <T>(chemin: string) =>
  ({ signal }: { signal: AbortSignal }) =>
    requete<T>(chemin, { signal });
const envoyer = <T>(methode: 'POST' | 'PUT', chemin: string, corps?: unknown) =>
  requete<T>(chemin, { methode, corps });

export const useActivation = () =>
  useQuery({
    queryKey: ['social', 'activation'],
    queryFn: lire<ActivationSociale>('/social/activation'),
    retry: false,
  });
export const useSynthese = () =>
  useQuery({
    queryKey: ['social', 'synthese'],
    queryFn: lire<SyntheseSociale>('/social/synthese'),
    retry: false,
  });
export const useProgrammes = (actif = true) =>
  useQuery({
    queryKey: ['social', 'programmes'],
    queryFn: lire<Liste<ProgrammeSocial>>('/social/programmes'),
    retry: false,
    enabled: actif,
  });
export const useDemandes = (
  f: { programme_id?: string; etat?: string; recherche?: string },
  actif = true,
) =>
  useQuery({
    queryKey: ['social', 'demandes', f],
    queryFn: ({ signal }) =>
      requete<Liste<ResumeDemandeSociale>>('/social/demandes', { signal, parametres: f }),
    retry: false,
    enabled: actif,
  });
export const useDemande = (id: string | undefined) =>
  useQuery({
    queryKey: ['social', 'demande', id ?? ''],
    queryFn: lire<DemandeSociale>(`/social/demandes/${id}`),
    enabled: Boolean(id),
    retry: false,
  });

export const modifierActivation = (actif: boolean, motif: string) =>
  envoyer<ActivationSociale>('PUT', '/social/activation', { actif, motif });
export const creerProgramme = (c: CreationProgrammeSocial) =>
  envoyer<ProgrammeSocial>('POST', '/social/programmes', c);
export const versionnerCriteres = (id: string, c: VersionCriteresSocial) =>
  envoyer<ProgrammeSocial>('POST', `/social/programmes/${id}/criteres`, c);
export const changerEtatProgramme = (id: string, etat: 'ouvert' | 'clos', motif: string) =>
  envoyer<ProgrammeSocial>('PUT', `/social/programmes/${id}/etat`, { etat, motif });
export const creerDemande = (c: CreationDemandeSociale) =>
  envoyer<{ id: string; numero: string; doublons: number }>('POST', '/social/demandes', c);
export const evaluer = (id: string, c: EvaluationCriteres) =>
  envoyer<DemandeSociale>('PUT', `/social/demandes/${id}/evaluation`, c);
export const noter = (id: string, contenu: string) =>
  envoyer<DemandeSociale>('POST', `/social/demandes/${id}/notes`, { contenu });
export const examinerDoublon = (
  id: string,
  doublon: string,
  conclusion: 'doublon_confirme' | 'non_doublon',
  motif: string,
) =>
  envoyer<DemandeSociale>('POST', `/social/demandes/${id}/doublons/${doublon}/examen`, {
    conclusion,
    motif,
  });
export const transmettre = (id: string) =>
  envoyer<DemandeSociale>('POST', `/social/demandes/${id}/transmission`);
export const classer = (id: string, motif: string) =>
  envoyer<DemandeSociale>('POST', `/social/demandes/${id}/classement`, { motif });
export const decider = (id: string, c: DecisionSocialeSaisie) =>
  envoyer<DemandeSociale>('POST', `/social/demandes/${id}/decision`, c);
export const lierEngagement = (id: string, engagement_id: string) =>
  envoyer<DemandeSociale>('PUT', `/social/demandes/${id}/engagement`, { engagement_id });
export const suivre = (id: string, c: SuiviSocialSaisi) =>
  envoyer<DemandeSociale>('POST', `/social/demandes/${id}/suivis`, c);
export const messageUsager = (id: string, message_usager: string) =>
  envoyer<DemandeSociale>('PUT', `/social/demandes/${id}/message-usager`, { message_usager });

export function deposerPiece(
  id: string,
  fichier: File,
  type_piece: string,
): Promise<DemandeSociale> {
  const corps = new FormData();
  corps.set('fichier', fichier);
  corps.set('type_piece', type_piece);
  return requete<DemandeSociale>(`/social/demandes/${id}/pieces`, { methode: 'POST', corps });
}

export async function telechargerPiece(id: string, piece: string, nom: string): Promise<void> {
  const r = await requete<Response>(`/social/demandes/${id}/pieces/${piece}/contenu`, {
    brut: true,
  });
  telechargerBlob(nom, await r.blob());
}

export async function exporterDemandes(f: { programme_id?: string; etat?: string }): Promise<void> {
  const r = await requete<Response>('/social/demandes/export', { brut: true, parametres: f });
  telechargerBlob('demandes-sociales.csv', await r.blob());
}
